import { usePhone } from '@/state/phone';
import { useEmail } from '@/state/email';
import { useLife } from '@/state/life';
import { useNotifications } from '@/state/notifications';
import { useXClone } from '@/state/xclone';
import { useMarkets } from '@/state/markets';
import { useMeta } from '@/state/meta';
import { usePower } from '@/state/power';
import { useCinematic } from '@/components/CinematicTakeover';
import { sounds } from '@/audio/synth';
import { startPressConference } from './pressConference';
import { startTrumpShoutoutArc } from './trumpShoutoutArc';
import { startMomEmergencyArc } from './momEmergencyArc';
import { startBotFollowersArc } from './botFollowersArc';
import { startDogReturnsArc } from './dogReturnsArc';
import { useWallet } from '@/state/wallet';

/**
 * Multi-step authored arcs — fire on threshold then schedule follow-up beats
 * via setTimeout. Real MDX engine deferred; this is the manual version.
 *
 * Each arc only fires once per run (tracked via meta.loreCodex). Reset on
 * ascension by clearing arc-specific lore IDs (handled by ascend()).
 */

const fired = new Set<string>();

export function maybeStartArc(id: string, condition: () => boolean, run: () => void): void {
  if (fired.has(id)) return;
  if (!condition()) return;
  fired.add(id);
  useMeta.getState().recordLore(id, ARC_TITLES[id] ?? id);
  run();
}

export function clearAllArcsForAscension(): void {
  fired.clear();
}

const ARC_TITLES: Record<string, string> = {
  'arc-wife-divorce': 'Wife Divorce Arc',
  'arc-irs-audit': 'IRS Audit Arc',
  'arc-going-viral': 'Going Viral Arc',
  'arc-sec-investigation': 'SEC Investigation Arc',
  'arc-wife-reconciliation': 'Wife Reconciliation Arc',
  'arc-podcast-circuit': 'Podcast Circuit Arc',
  'arc-hospital': 'Hospital Arc',
  'arc-fbi-raid': 'FBI Raid Arc',
  'arc-press-conference': 'Press Conference',
  'arc-run-for-office-pitch': 'Run For Office',
  'arc-charity-gala': 'Charity Gala',
  'arc-yacht-trip': 'Saudi Yacht Trip',
  'arc-rally-stand-in': 'Rally Stand-In',
  'arc-podcast-appearance': 'Joe Rogan Appearance',
  'arc-crypto-pump': 'Crypto Pump',
  'arc-court-hearing': 'SEC Court Hearing',
  'arc-run-for-office': 'Senate Campaign',
  'arc-tiktok-debut': 'TikTok Debut',
  'arc-hostile-takeover': 'Hostile Takeover',
  'arc-cult-founding': 'Cult Founding',
  'arc-trump-shoutout': 'Presidential Shoutout',
  'arc-affair': 'The Affair',
  'arc-mom-emergency': 'Mom Emergency',
  'arc-fed-decision': 'Fed Day',
  'arc-earnings-call': 'Earnings Call',
  'arc-tier-billionaire': 'First Billion',
  'arc-tier-centibillionaire': '$100B',
  'arc-tier-trillionaire': 'Trillionaire',
  'arc-tier-god': 'God Tier',
  'arc-tax-season': 'Tax Season',
  'arc-bot-followers': 'Bot Followers Audit',
  'arc-dog-returns': 'Bartholomew Returns',
  'arc-pardon': 'Presidential Pardon',
  'arc-foreign-asset': 'Foreign Asset',
  'arc-bloomberg-interview': 'Bloomberg Interview',
  'arc-newspaper-editorial-crisis': 'Newspaper Editorial Crisis',
  'arc-sports-team-scandal': 'Sports Team League Review',
};

// ─── Arc 1: Wife Divorce ───────────────────────────────────────────────
//
// Fires when marriage <= -50.
// Beat 1: she sends "i think you should know..." (immediate)
// Beat 2: she sends a screenshot of brokerage statement (5s)
// Beat 3: lawyer office sends an email (45s)
// Beat 4: BREAKING cinematic fires (90s)
// Beat 5: divorce settlement email with $40K cost (180s)
// Beat 6: random Phone DM from "ChrissyMatch" dating-app NPC (240s)

export function processArcs(): void {
  const life = useLife.getState();
  const power = usePower.getState();

  // Press Conference: triggered once when notoriety crosses 60
  maybeStartArc('arc-press-conference', () => power.notoriety >= 60, () => {
    startPressConference();
  });

  // Trump shoutout: triggered when notoriety >= 65 + maga rep > 20
  maybeStartArc('arc-trump-shoutout', () => power.notoriety >= 65 && power.tribeRep.maga > 20, () => {
    startTrumpShoutoutArc();
  });

  // Mom emergency: triggered when mom rep <= 35 (player ignored her at least once)
  // Mom starts at 60; declining mom-rent is -25 → 60 - 25 = 35 → triggers
  maybeStartArc('arc-mom-emergency', () => life.relationships.mom <= 35, () => {
    startMomEmergencyArc();
  });

  // Bot followers audit: triggered when followers >= 100K
  maybeStartArc('arc-bot-followers', () => useXClone.getState().followers >= 100_000, () => {
    startBotFollowersArc();
  });

  // Dog returns: only if dog was sold AND sanity is high AND wife rep recovering AND 30% chance
  maybeStartArc('arc-dog-returns', () => {
    const dog = useWallet.getState().possessions.find((p) => p.id === 'dog');
    return dog != null && !dog.available && life.sanity >= 60 && life.relationships.wife >= 20 && Math.random() < 0.3;
  }, () => {
    startDogReturnsArc();
  });

  // ─── Newspaper editorial crisis ───────────────────────────────────────
  // Fires when player owns the newspaper AND notoriety > 30. The new editor
  // immediately resigns; replacement also resigns; eventually you get an
  // ultimatum to write the editorial yourself.
  maybeStartArc('arc-newspaper-editorial-crisis', () =>
    useWallet.getState().ownsNewspaper && usePower.getState().notoriety >= 30, () => {
    useEmail.getState().receive({
      fromNpc: 'editor',
      fromAddress: 'editor@thepaper.example',
      subject: 'My resignation, effective immediately',
      body: "Sir,\n\nI have served as Editor-in-Chief of The Paper for 14 years. In that time we have weathered four owners, three lawsuits, and a brief flirtation with becoming a wellness blog. I have endured all of it.\n\nI will not, however, endure this.\n\nThe staff has been informed. The masthead will reflect the change tomorrow.\n\n— Margaret Chen, former Editor-in-Chief",
    });
    useNotifications.getState().push({ surfaceId: 'email', severity: 'urgent', text: 'editor: resigning immediately' });
    sounds.notifyAlert();

    setTimeout(() => {
      useEmail.getState().receive({
        fromNpc: 'editor',
        fromAddress: 'editor@thepaper.example',
        subject: 'My resignation, effective immediately',
        body: "Sir,\n\nThe board appointed me as interim Editor-in-Chief 4 days ago. I would like to formally resign.\n\nI will not, in writing, give a reason. I have spoken to a labor lawyer. The reason is in their notes.\n\n— David Park, briefly Editor-in-Chief",
      });
      useNotifications.getState().push({ surfaceId: 'email', severity: 'important', text: 'second editor: resigning' });
    }, 60_000);

    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'BREAKING: The Paper has lost two editors in 4 days. Industry observers note this is an unusual pace, even by current standards.',
        likes: 14400, reposts: 4200,
      });
    }, 90_000);

    setTimeout(() => {
      useEmail.getState().receive({
        fromNpc: 'lawyer',
        fromAddress: 'l.markowitz@markowitzandsons.law',
        subject: 'Editorial-page liability',
        body: "Per our retainer:\n\nIf you write the editorial yourself, every word becomes legally yours. Every libel claim becomes legally yours. Every defamation suit becomes legally yours.\n\nI advise you to find a third editor. I have a name. They cost $400K/year. They will resign within 6 months. That is the going rate.\n\n— Lou Markowitz, Esq.",
      });
      useMarkets.getState().addCash(-400_000);
      useNotifications.getState().push({ surfaceId: 'email', severity: 'urgent', text: '-$400K — replacement editor hired (6-month flight risk)' });
    }, 180_000);
  });

  // ─── Sports team scandal ─────────────────────────────────────────────
  // Fires when owns a sports team and FBI heat > 30. Implies financial
  // entanglement that has, allegedly, attracted attention.
  maybeStartArc('arc-sports-team-scandal', () =>
    useWallet.getState().ownsSportsTeam && useLife.getState().fbiHeat >= 30, () => {
    useEmail.getState().receive({
      fromNpc: 'commissioner',
      fromAddress: 'office@league.example',
      subject: 'Notice of internal review',
      body: "Sir,\n\nThe League has opened a routine internal review of franchise ownership structures across all teams. As part of this review, we will be requesting documentation regarding fund flows, beneficial ownership, and any agreements with sovereign-affiliated entities.\n\nWe expect your full cooperation. We expect this within 10 business days.\n\n— Office of the Commissioner",
    });
    useNotifications.getState().push({ surfaceId: 'email', severity: 'urgent', text: 'League: internal review, 10-day deadline' });
    sounds.notifyAlert();

    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'ESPN_NewsDesk', authorTribe: 'press',
        text: 'BREAKING: League sources confirm an "internal review" has begun on a franchise. Sources will not name the franchise. We have, however, narrowed it down to one team. We will not be saying which.',
        likes: 28000, reposts: 7600,
      });
    }, 60_000);

    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'lawyer', fromNpc: 'lawyer',
        text: 'sports leagues are not regulators. they are stricter than regulators. they have their own court. their court has no appeals.\n\nwe are paying \$2,800 to draft a response. we will draft 14. they will reject all 14.\n\n— Lou',
      });
      useMarkets.getState().addCash(-2_800);
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'lawyer: -$2,800 league response' });
    }, 90_000);
  });

  // Tier ascension cinematics — quiet ego-trip moments at major wealth crossings
  maybeStartArc('arc-tier-billionaire', () => useMarkets.getState().cash >= 1_000_000_000, () => {
    useCinematic.getState().fire({
      id: `tier-billionaire-${Date.now()}`,
      type: 'ascension',
      headline: 'YOU ARE A BILLIONAIRE.',
      subhead: '14 figures. nobody owes you anything. you owe nobody anything. CNN is on line 2. let it ring.',
      durationMs: 7000,
    });
    sounds.notifyAlert();
    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'BREAKING: a "well-known retail trader" has crossed \$1B in net worth. analysts split on whether to call him a "self-made billionaire" or "very lucky retail trader." both terms apply.',
        likes: 32000, reposts: 9800,
      });
    }, 5_000);
  });

  maybeStartArc('arc-tier-centibillionaire', () => useMarkets.getState().cash >= 100_000_000_000, () => {
    useCinematic.getState().fire({
      id: `tier-centi-${Date.now()}`,
      type: 'ascension',
      headline: 'YOU ARE A CENTIBILLIONAIRE.',
      subhead: '\$100B. you have entered the Bloomberg index in 47th place.',
      durationMs: 7500,
    });
    sounds.notifyAlert();
  });

  maybeStartArc('arc-tier-trillionaire', () => useMarkets.getState().cash >= 1_000_000_000_000, () => {
    useCinematic.getState().fire({
      id: `tier-trillionaire-${Date.now()}`,
      type: 'ascension',
      headline: 'YOU ARE A TRILLIONAIRE.',
      subhead: '\$1T. you alone are a sovereign wealth fund. several countries have requested loans. the IMF has set up a meeting.',
      durationMs: 8000,
    });
    sounds.notifyAlert();
    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'CNBCSquawk', authorTribe: 'cnbc',
        text: 'we now go to a panel of analysts who will spend the next 4 hours discussing whether \$TRILLIONAIRE is a real word.',
        likes: 67000, reposts: 18400,
      });
    }, 6_000);
  });

  maybeStartArc('arc-tier-god', () => useMarkets.getState().cash >= 10_000_000_000_000, () => {
    useCinematic.getState().fire({
      id: `tier-god-${Date.now()}`,
      type: 'ascension',
      headline: 'NUMBERS STOP MEANING THINGS.',
      subhead: 'you have \$10T. economists are reportedly "in their cars." the Fed has called you. the Vatican has called you. you are not picking up.',
      durationMs: 9000,
    });
    sounds.notifyAlert();
    setTimeout(() => {
      useCinematic.getState().fire({
        id: `god-followup-${Date.now()}`,
        type: 'breaking',
        headline: 'A NEW WEALTH TIER HAS BEEN ADDED FOR YOU.',
        subhead: 'tier name: "God Tier." threshold: "you, specifically." next milestone: undefined.',
        durationMs: 6000,
      });
    }, 7_000);
  });

  // Senator pitches "you should run" when notoriety crosses 80
  maybeStartArc('arc-run-for-office-pitch', () => power.notoriety >= 80, () => {
    if (!usePhone.getState().contacts['senator']) {
      usePhone.setState((s) => ({
        ...s,
        contacts: { ...s.contacts, 'senator': { id: 'senator', displayName: 'Sen. Brock Halligan' } },
      }));
    }
    usePhone.getState().receiveMessage({
      threadId: 'senator', fromNpc: 'senator',
      text: "i\'ve been talking to people. quietly. they think you should run. for senate. yes. seriously. yes.",
      actions: [
        { id: 'a', label: "i'll run.", effectId: 'run-accept' },
        { id: 'd', label: 'absolutely not', effectId: 'run-decline', destructive: true },
      ],
    });
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'senator: you should run for senate' });
    sounds.notifyAlert();
  });

  maybeStartArc('arc-wife-divorce', () => life.relationships.wife <= -50, () => {
    usePhone.getState().receiveMessage({
      threadId: 'wife', fromNpc: 'wife',
      text: 'i think you should know — my lawyer reached out to your accountant. you can stop ignoring me now. it\'s already done.',
    });
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: "wife: it's already done." });
    sounds.notifyAlert();

    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'wife', fromNpc: 'wife',
        text: '[image attached: a screenshot of your brokerage account, dated yesterday, showing a -42% day]\n\ndon\'t bother responding.',
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'wife: [screenshot]' });
    }, 5_000);

    setTimeout(() => {
      useEmail.getState().receive({
        fromNpc: 'lawyer-other-side',
        fromAddress: 'service@hartlow-and-fenn.law',
        subject: 'NOTICE — Petition for Dissolution of Marriage',
        body: "Sir,\n\nThis is formal notice that our client has filed a petition for dissolution of marriage. The petition seeks an equal division of marital assets including all brokerage accounts, the residence at [redacted], and the dog.\n\nResponse required within 21 days.\n\n— Hartlow & Fenn LLP",
      });
      useNotifications.getState().push({ surfaceId: 'email', severity: 'urgent', text: 'NOTICE — Dissolution of Marriage' });
      sounds.marginCall();
    }, 45_000);

    setTimeout(() => {
      useCinematic.getState().fire({
        id: 'cin-divorce-filed',
        type: 'breaking',
        headline: 'WIFE FILES.',
        subhead: 'and not for an extension. she has been preparing this for months. you missed the signs because you were watching $TARI.',
        durationMs: 6000,
      });
    }, 90_000);

    setTimeout(() => {
      useEmail.getState().receive({
        fromNpc: 'lawyer-other-side',
        fromAddress: 'service@hartlow-and-fenn.law',
        subject: 'PROPOSED SETTLEMENT — Final Offer',
        body: "Sir,\n\nThe attached proposes the following: $40,000 lump sum to our client, $1,200/month support, and Bartholomew. Counter-proposals will be reviewed but not accepted.\n\nRespectfully,\n— Hartlow & Fenn LLP",
      });
      // The cost actually charges
      useMarkets.getState().addCash(-40_000);
      useNotifications.getState().push({ surfaceId: 'wallet', severity: 'urgent', text: '-$40,000 settlement' });
    }, 180_000);

    setTimeout(() => {
      // Add a new contact to phone
      usePhone.setState((s) => ({
        ...s,
        contacts: { ...s.contacts, 'chrissy-match': { id: 'chrissy-match', displayName: 'Chrissy' } },
      }));
      usePhone.getState().receiveMessage({
        threadId: 'chrissy-match', fromNpc: 'chrissy-match',
        text: 'hey i saw your profile. it just says "TRADER" and a lambo emoji. tell me more about yourself ⚡',
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'chrissy: tell me more about yourself ⚡' });
    }, 240_000);
  });

  // ─── Arc 2: IRS Audit ─────────────────────────────────────────────────
  maybeStartArc('arc-irs-audit', () => life.irsHeat >= 60, () => {
    useEmail.getState().receive({
      fromNpc: 'irs',
      fromAddress: 'audit-services@irs.gov',
      subject: 'NOTICE OF PROPOSED ADJUSTMENT',
      body: "Sir,\n\nWe have identified discrepancies in your reported income for tax years 2024-2026. The proposed additional tax owed is $128,400, plus penalties and interest.\n\nYou have 30 days to respond.\n\n— Internal Revenue Service",
    });
    useNotifications.getState().push({ surfaceId: 'email', severity: 'urgent', text: 'IRS: Proposed Adjustment' });

    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'lawyer', fromNpc: 'lawyer',
        text: 'IRS sent a CP2000. do not respond yourself. if you respond yourself i charge double when i fix it. say nothing. i\'ll be in touch.',
        actions: [
          { id: 'engage', label: 'engage Markowitz', effectId: 'offshore-clean-accept' },
          { id: 'ignore', label: 'ignore', effectId: 'mom-rent-decline', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'lawyer: do not respond yourself.' });
    }, 30_000);

    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'sources: a "well-known retail trader" is being audited by the IRS for $128K in unreported gains. WSJ has reached out for comment.',
        likes: 4400, reposts: 950,
      });
    }, 60_000);

    setTimeout(() => {
      useCinematic.getState().fire({
        id: 'cin-irs-court',
        type: 'breaking',
        headline: 'IRS HEARING SCHEDULED',
        subhead: 'a date with three letters has been set. you cannot reschedule. you cannot ghost the IRS.',
        durationMs: 5500,
      });
    }, 120_000);
  });

  // ─── Arc 4: SEC Investigation ────────────────────────────────────────
  maybeStartArc('arc-sec-investigation', () => life.secHeat >= 60, () => {
    useEmail.getState().receive({
      fromNpc: 'sec',
      fromAddress: 'enforcement@sec.gov',
      subject: 'NOTICE OF FORMAL INVESTIGATION',
      body: "Sir,\n\nThe Division of Enforcement has opened a formal investigation into your trading activity over the period [redacted].\n\nYou are hereby served with a subpoena for all communications and trade records.\n\nResponse required within 14 days. Failure to respond may result in additional charges.\n\n— Securities and Exchange Commission",
    });
    useNotifications.getState().push({ surfaceId: 'email', severity: 'urgent', text: 'SEC: NOTICE OF FORMAL INVESTIGATION' });

    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'sources: SEC has opened a formal investigation into a "well-known retail trader". the trader has not commented because their lawyer literally tied them to a chair.',
        likes: 8400, reposts: 2200,
      });
    }, 30_000);

    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'lawyer', fromNpc: 'lawyer',
        text: 'they have your trades. they have your DMs. they have screenshots of your finsta. settlement is the move. it\'s $50K. or we go to trial. i charge $1,400/h either way.',
        actions: [
          { id: 'a', label: 'pay settlement', effectId: 'sec-settle-accept' },
          { id: 'd', label: 'go to trial', effectId: 'sec-go-to-trial', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'lawyer: settlement is the move' });
    }, 75_000);

    setTimeout(() => {
      useCinematic.getState().fire({
        id: 'cin-sec-court',
        type: 'breaking',
        headline: 'SEC HEARING SCHEDULED',
        subhead: 'three letters now active in your life. they will not be the last.',
        durationMs: 5500,
      });
    }, 130_000);
  });

  // ─── Arc 5: Wife Reconciliation ──────────────────────────────────────
  // Fires when wife rep recovers above +50 (post-recovery, you've been redeeming)
  maybeStartArc('arc-wife-reconciliation', () => life.relationships.wife >= 50 && Math.random() < 0.5, () => {
    usePhone.getState().receiveMessage({
      threadId: 'wife', fromNpc: 'wife',
      text: "okay. that was a really nice text.",
    });
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'wife: that was a really nice text' });

    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'wife', fromNpc: 'wife',
        text: 'dinner saturday? not a date. just dinner.',
      });
    }, 60_000);

    setTimeout(() => {
      useCinematic.getState().fire({
        id: 'cin-wife-recon',
        type: 'welcome-back',
        headline: 'YOU GET ANOTHER CHANCE.',
        subhead: 'do not waste this one. she will not give a third.',
        durationMs: 5000,
      });
    }, 150_000);
  });

  // ─── Arc 7: Hospital ──────────────────────────────────────────────────
  // Fires when health drops below 20. Realistic consequences for a degenerate
  // trader who didn't sleep, ate at his desk, drank too much during JPow speeches.
  maybeStartArc('arc-hospital', () => life.health <= 20, () => {
    usePhone.getState().receiveMessage({
      threadId: 'broker', fromNpc: 'broker',
      text: 'you collapsed at your desk. you\'re fine. probably. an ambulance has been dispatched. don\'t panic.',
    });
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'you collapsed' });
    sounds.marginCall();

    setTimeout(() => {
      useEmail.getState().receive({
        fromNpc: 'hospital',
        fromAddress: 'billing@grace-medical.org',
        subject: 'Hospital invoice — itemized',
        body: "Patient,\n\nServices rendered: 1 ambulance ride, 1 night stay, 4 saline drips, 1 doctor consultation.\n\nTotal: \$47,400.\n\nYour insurance has denied 100% of the claim. They cite 'pre-existing recklessness.'\n\nPayment due in 30 days.\n\n— Grace Medical Center",
      });
      useNotifications.getState().push({ surfaceId: 'email', severity: 'urgent', text: 'Hospital: \$47,400 invoice' });
    }, 60_000);

    setTimeout(() => {
      useCinematic.getState().fire({
        id: 'cin-hospital',
        type: 'breaking',
        headline: 'YOU WOKE UP IN A HOSPITAL.',
        subhead: 'doctor says: "you have to sleep at some point." you ask if FOMC sleeps. doctor leaves the room.',
        durationMs: 5500,
      });
    }, 120_000);

    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'mom', fromNpc: 'mom',
        text: 'sweetie?? i saw you on the news. you\'re in the hospital?? are you eating??',
      });
    }, 180_000);
  });

  // ─── Arc 8: FBI Raid ──────────────────────────────────────────────────
  // Fires when FBI heat ≥ 75. The serious one.
  maybeStartArc('arc-fbi-raid', () => life.fbiHeat >= 75, () => {
    useCinematic.getState().fire({
      id: 'cin-fbi-raid',
      type: 'breaking',
      headline: 'THE FBI IS AT YOUR DOOR.',
      subhead: 'they have a warrant. they have your laptop. they have your phone. they will return them in approximately never.',
      durationMs: 8000,
    });
    sounds.marginCall();

    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'lawyer', fromNpc: 'lawyer',
        text: 'STOP TALKING. SAY NOTHING. EAT THIS MESSAGE.',
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'lawyer: STOP TALKING' });
    }, 5_000);

    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'ReutersFlash', authorTribe: 'press',
        text: 'BREAKING: FBI raids "well-known retail trader." sources say charges "imminent." CNBN has cleared 4 hours of programming. CNBC has cleared 8.',
        likes: 18400, reposts: 5200,
      });
    }, 30_000);

    setTimeout(() => {
      useEmail.getState().receive({
        fromNpc: 'fbi',
        fromAddress: 'no-reply@fbi.gov',
        subject: 'NOTICE OF SEARCH WARRANT EXECUTION',
        body: "This notice confirms that a search warrant was executed at your residence and place of business at [time].\n\nItems seized: laptops, phones, 1 dog, 14 boxes of paper records, 1 notebook labeled \"do not give to FBI.\"\n\n— Federal Bureau of Investigation",
      });
    }, 90_000);

    setTimeout(() => {
      // The IRS jumps in too — opportunistic
      useEmail.getState().receive({
        fromNpc: 'irs',
        fromAddress: 'enforcement@irs.gov',
        subject: 'In light of recent events',
        body: "We just want you to know we\'re also here.\n\nWe have always been here.\n\nWe will continue to be here.\n\n— IRS",
      });
    }, 150_000);
  });

  // ─── Arc 6: Podcast Circuit ──────────────────────────────────────────
  // Fires after 3+ Phone contacts have been added (you've become "someone")
  maybeStartArc('arc-podcast-circuit', () => Object.keys(usePhone.getState().contacts).length >= 6, () => {
    useEmail.getState().receive({
      fromNpc: 'rogan-bookings',
      fromAddress: 'bookings@joeroganpodcast.spotify.com',
      subject: 'Joe wants you on the show',
      body: "hi.\n\nJoe wants you on. 3 hours minimum. you can talk about whatever. nothing is off the table. nothing is on the record. nothing makes it past the wash if Joe doesn't like you.\n\ndate: TBD. tape next week.\n\n— bookings",
    });
    useNotifications.getState().push({ surfaceId: 'email', severity: 'important', text: 'Joe Rogan: wants you on the show' });

    setTimeout(() => {
      usePhone.getState().receiveMessage({
        threadId: 'podcaster', fromNpc: 'podcaster',
        text: 'BRO. Joe asked you on. that\'s not a podcast that\'s a 4-hour rebrand. say yes.',
        actions: [
          { id: 'a', label: 'YES — go on Rogan', effectId: 'rogan-accept' },
          { id: 'd', label: 'decline', effectId: 'rogan-decline', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'Joe Rogan invite — choose' });
    }, 30_000);
  });

  // ─── Arc 3: Going Viral ───────────────────────────────────────────────
  maybeStartArc('arc-going-viral', () => useXClone.getState().followers >= 50_000, () => {
    useCinematic.getState().fire({
      id: 'cin-viral',
      type: 'breaking',
      headline: 'YOUR LAST POST IS GOING SERIOUSLY VIRAL.',
      subhead: '50K+ shares. journalists are sliding into your DMs. you have crossed the threshold from "trader" to "person." good luck.',
      durationMs: 5000,
    });

    setTimeout(() => {
      useEmail.getState().receive({
        fromNpc: 'cnn-bookings',
        fromAddress: 'bookings@cable-news-network.tv',
        subject: 'CNN Live segment opportunity',
        body: 'Hi,\n\nWe\'d like to have you on Anderson tonight at 7pm ET. Topic: your tweet. Pre-tape if needed. Car will be sent.\n\n— Bookings desk',
      });
      useNotifications.getState().push({ surfaceId: 'email', severity: 'important', text: 'CNN: live segment tonight 7pm' });
    }, 30_000);

    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'A retail trader\'s tweet just moved $TARI 2%. who are they? what does their portfolio look like? is this a 14-tweet thread? unfortunately yes.',
        likes: 18400, reposts: 4400,
      });
    }, 60_000);

    setTimeout(() => {
      // Sponsorship offer
      usePhone.getState().receiveMessage({
        threadId: 'broker', fromNpc: 'broker',
        text: 'BrainJuice Energy is offering you $50K to mention them in a tweet. just say "fueled by BrainJuice." the lawyers say it\'s fine probably.',
        actions: [
          { id: 'accept', label: 'accept', effectId: 'senator-sponsor-accept' },
          { id: 'decline', label: 'decline', effectId: 'senator-sponsor-decline', destructive: true },
        ],
      });
    }, 120_000);
  });
}
